import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, Laptop, DollarSign, Clock, Target, Brain, Shield, CheckCircle, XCircle, AlertCircle, HelpCircle, TrendingDown, Zap } from 'lucide-react';

const FinancialPlannerVsSoftwarePage = () => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState('software');
  const [selectedCountry, setSelectedCountry] = useState('US'); // US or UK

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens AI</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-6">
            <HelpCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">Compare Your Options</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Financial Planner
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> vs </span>
            Financial Software
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Deciding between hiring a financial planner or using financial planning software? We break down
            the costs, benefits, and ideal use cases to help you make the right choice.
          </p>

          {/* Country Selector */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-xl shadow-md p-2">
              <button
                onClick={() => setSelectedCountry('US')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedCountry === 'US'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">🇺🇸</span>
                <span>United States</span>
              </button>
              <button
                onClick={() => setSelectedCountry('UK')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedCountry === 'UK'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">🇬🇧</span>
                <span>United Kingdom</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Comparison Toggle */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-2 flex">
            <button
              onClick={() => setSelectedOption('planner')}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                selectedOption === 'planner'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Financial Planner</span>
            </button>
            <button
              onClick={() => setSelectedOption('software')}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                selectedOption === 'software'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Laptop className="w-5 h-5" />
              <span>Financial Software</span>
            </button>
          </div>
        </div>

        {/* Detailed Comparison Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Factor</th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-blue-600">
                    <div className="flex items-center justify-center space-x-2">
                      <Users className="w-4 h-4" />
                      <span>Financial Planner</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-purple-600">
                    <div className="flex items-center justify-center space-x-2">
                      <Laptop className="w-4 h-4" />
                      <span>Financial Software</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Cost */}
                <tr>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Annual Cost</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-red-600 font-bold text-lg">
                      {selectedCountry === 'US' ? '$2,000 - $20,000+' : '£1,500 - £15,000+'}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">1-2% of assets or hourly fees</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-green-600 font-bold text-lg">
                      {selectedCountry === 'US' ? '$0 - $65/year' : '£0 - £50/year'}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      PlanLens: Free or {selectedCountry === 'US' ? '$65/year' : '£49.99/year'}
                    </p>
                  </td>
                </tr>

                {/* Time Commitment */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Time Commitment</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-gray-900">Multiple meetings</div>
                    <p className="text-xs text-gray-600 mt-1">Initial + quarterly reviews</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-gray-900">On your schedule</div>
                    <p className="text-xs text-gray-600 mt-1">5-30 min per session</p>
                  </td>
                </tr>

                {/* Portfolio Tracking */}
                <tr>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Portfolio Tracking</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Quarterly reports</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Real-time dashboards</p>
                  </td>
                </tr>

                {/* Retirement Planning */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Target className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Retirement Planning</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Personalized strategy</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Monte Carlo simulations</p>
                  </td>
                </tr>

                {/* Investment Recommendations */}
                <tr>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Brain className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Investment Advice</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Regulated advice</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <XCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Analysis only (no advice)</p>
                  </td>
                </tr>

                {/* Tax Planning */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Tax Planning</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Comprehensive strategies</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-amber-600 mx-auto mb-1">Partial</div>
                    <p className="text-xs text-gray-600">Account type tracking</p>
                  </td>
                </tr>

                {/* Estate Planning */}
                <tr>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Users className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Estate Planning</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Wills, trusts, inheritance</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <XCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Not included</p>
                  </td>
                </tr>

                {/* Accessibility */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Zap className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">Accessibility</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-gray-900">Scheduled only</div>
                    <p className="text-xs text-gray-600 mt-1">Business hours</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-green-600">24/7</div>
                    <p className="text-xs text-gray-600 mt-1">Anytime, anywhere</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* When to Use Each Option */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Financial Planner */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Choose a Financial Planner If:</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Complex Financial Situation</h3>
                  <p className="text-sm text-gray-600">
                     Business ownership, inheritance, or Complex estate planning
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Need Regulated Advice</h3>
                  <p className="text-sm text-gray-600">
                    Want specific investment recommendations and someone legally responsible for advice
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Comprehensive Planning</h3>
                  <p className="text-sm text-gray-600">
                    Need estate planning, tax optimization, and coordinated financial strategy
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Prefer Human Interaction</h3>
                  <p className="text-sm text-gray-600">
                    Value personal relationships and want someone to hold you accountable
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Major Life Events</h3>
                  <p className="text-sm text-gray-600">
                    Going through divorce, selling a business, retiring soon and need advice, or receiving inheritance
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Average UK Cost:</span> £1,500-£3,000/year for basic planning,
                up to 1-2% of assets under management (AUM) for comprehensive wealth management.
              </p>
            </div>
          </div>

          {/* Financial Software */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Laptop className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Choose Financial Software If:</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Straightforward Finances</h3>
                  <p className="text-sm text-gray-600">
                    Salaried employee with standard accounts (ISAs, pensions, savings)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">DIY Investor</h3>
                  <p className="text-sm text-gray-600">
                    Comfortable making your own investment decisions and want analytical tools
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Cost-Conscious</h3>
                  <p className="text-sm text-gray-600">
                    Want to minimize fees and keep more of your returns
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Track & Monitor</h3>
                  <p className="text-sm text-gray-600">
                    Need portfolio tracking and analysis, retirement projections, and goal planning
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Building Wealth</h3>
                  <p className="text-sm text-gray-600">
                    Early-to-mid career, accumulating assets, don't need complex planning yet
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">PlanLens Cost:</span> Free with core features,
                or {selectedCountry === 'US'
                  ? '$5.99/month ($65/year)'
                  : '£4.99/month (£49.99/year)'} for premium AI insights and advanced analysis.
              </p>
            </div>
          </div>
        </div>

        {/* Cost Comparison Over Time */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">10-Year Cost Comparison</h2>
          <p className="text-center text-gray-600 mb-8">
            See how much you could save using financial software vs hiring a planner
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6">
              <div className="flex items-center justify-center mb-4">
                <TrendingDown className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-center font-bold text-gray-900 mb-2">Financial Planner</h3>
              <p className="text-center text-sm text-gray-600 mb-3">
                1% AUM on {selectedCountry === 'US' ? '$350,000' : '£250,000'} portfolio
              </p>
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-1">
                  {selectedCountry === 'US' ? '$35,000' : '£25,000'}
                </div>
                <p className="text-xs text-gray-600">
                  Over 10 years ({selectedCountry === 'US' ? '$3,500' : '£2,500'}/year)
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
              <div className="flex items-center justify-center mb-4">
                <DollarSign className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-center font-bold text-gray-900 mb-2">Robo-Advisor</h3>
              <p className="text-center text-sm text-gray-600 mb-3">
                0.25% AUM on {selectedCountry === 'US' ? '$350,000' : '£250,000'} portfolio
              </p>
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-600 mb-1">
                  {selectedCountry === 'US' ? '$8,750' : '£6,250'}
                </div>
                <p className="text-xs text-gray-600">
                  Over 10 years ({selectedCountry === 'US' ? '$875' : '£625'}/year)
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
              <div className="flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-center font-bold text-gray-900 mb-2">PlanLens Premium</h3>
              <p className="text-center text-sm text-gray-600 mb-3">Flat fee (no % of assets)</p>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-1">
                  {selectedCountry === 'US' ? '$650' : '£499'}
                </div>
                <p className="text-xs text-gray-600">
                  Over 10 years ({selectedCountry === 'US' ? '$65' : '£49.99'}/year)
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
            <p className="text-lg font-bold text-gray-900 mb-2">
              Potential Savings with PlanLens: <span className="text-green-600">
                {selectedCountry === 'US' ? '$34,350' : '£24,501'}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              Over 10 years compared to a financial planner charging 1% AUM
            </p>
          </div>
        </div>

        {/* Hybrid Approach */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The Hybrid Approach: Best of Both Worlds
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Many savvy investors use software for day-to-day tracking and consult a planner for major decisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Use PlanLens For:</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Daily portfolio tracking and monitoring</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Retirement projections and scenario planning</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Goal tracking and net worth calculations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Portfolio analysis and risk metrics</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Consult a Planner For:</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Major life events (retirement, inheritance)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Complex tax planning and optimization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Estate planning, wills, and trusts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Annual financial health checkup</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-xl p-6 text-center">
            <p className="text-gray-700">
              <span className="font-semibold">Smart Strategy:</span> Use PlanLens for {selectedCountry === 'US' ? '$65/year + annual planner consultation for $750 = ' : '£49.99/year + annual planner consultation for £500 = '}
              <span className="font-bold text-green-600">
                {selectedCountry === 'US' ? ' $815/year total' : ' £550/year total'}
              </span> vs {selectedCountry === 'US' ? '$3,500/year' : '£2,500/year'} for full-service planning
            </p>
          </div>
        </div>

        {/* Key Considerations */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Considerations</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-amber-50 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Financial Planner Considerations</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Ensure they're FCA-regulated and fee-only (not commission-based)</li>
                    <li>• Understand their fee structure (AUM, hourly, or fixed)</li>
                    <li>• Ask about their investment philosophy and qualifications</li>
                    <li>• Check reviews and ask for references</li>
                    <li>• Confirm they have professional indemnity insurance</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Software Considerations</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Software provides analysis, not regulated advice</li>
                    <li>• You're responsible for your own investment decisions</li>
                    <li>• Requires basic financial literacy and self-discipline</li>
                    <li>• Check data security and privacy policies</li>
                    <li>• Ensure it supports your country (US/UK accounts)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Start with PlanLens—Upgrade to a Financial Planner When Needed
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Begin your financial journey with powerful software, then consult a financial advisor/planner for complex needs
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-12 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition shadow-xl hover:shadow-2xl text-lg"
          >
            Try PlanLens Free Today
          </button>
          <p className="text-sm mt-4 opacity-75">No credit card required • Free forever plan available</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © 2025 PlanLens. Compare financial planning options and make informed decisions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FinancialPlannerVsSoftwarePage;
